import PropTypes from 'prop-types';

// Every icon the app uses, as paths.
//
// It replaces boxicons and LineIcons: 935 kB and 612 kB of SVG font, plus their
// .ttf, .woff and .eot twins, downloaded in full so that a dozen glyphs could be
// shown. This file is under 3 kB and ships inside the bundle, so the icons
// arrive with the markup instead of a network round trip after it.
//
// Drawn on a 24-unit grid, stroked rather than filled, so they inherit the
// surrounding colour and stay even-weighted at any size.
const PATHS = {
  dots:      'M5 12h.01M12 12h.01M19 12h.01',
  chevron:   'M9 6l6 6-6 6',
  back:      'M15 6l-6 6 6 6',
  close:     'M6 6l12 12M18 6L6 18',
  location:  'M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11z M12 10h.01',
  volume:    'M11 5L6 9H3v6h3l5 4V5z M16 9a4 4 0 0 1 0 6',
  palette:   'M12 3a9 9 0 1 0 0 18h1.5a2 2 0 0 0 0-4H13a1.5 1.5 0 0 1 0-3h3a4 4 0 0 0 4-4 7 7 0 0 0-8-7z M7.5 11h.01M10 7.5h.01M14.5 7h.01',
  cog:       'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 9 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 9a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z',
  play:      'M7 4.5v15l12-7.5-12-7.5z',
  stop:      'M6 6h12v12H6z',
  refresh:   'M21 12a9 9 0 1 1-2.6-6.4 M21 3v6h-6',
  power:     'M12 3v9 M6.4 6.4a8 8 0 1 0 11.2 0',
  plus:      'M12 5v14M5 12h14',
  box:       'M21 8l-9-5-9 5 9 5 9-5z M3 8v8l9 5 9-5V8',
  phone:     'M7 2h10a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z M11 18h2',
  key:       'M14 7a4 4 0 1 1 3.5 4L16 12.5V15h-2.5v2H11v2.5H7.5V17l5-5A4 4 0 0 1 14 7z',
  signout:   'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4 M16 17l5-5-5-5 M21 12H9',
  warning:   'M12 9v4M12 17h.01 M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
  check:     'M20 6L9 17l-5-5',
  user:      'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  download:  'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
  search:    'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z M21 21l-4.3-4.3',
  moon:      'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z',
  sun:       'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10z M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
};

const Icon = ({ name, size = 18, className, title }) => {
  const d = PATHS[name];
  if (!d) return null;

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : 'true'}
      role={title ? 'img' : undefined}
      focusable="false"
    >
      {title && <title>{title}</title>}
      {d.split(' M').map((part, i) => (
        <path key={i} d={i === 0 ? part : 'M' + part} />
      ))}
    </svg>
  );
};

Icon.propTypes = {
  name: PropTypes.string.isRequired,
  size: PropTypes.number,
  className: PropTypes.string,
  title: PropTypes.string,
};

export default Icon;
